import { Html5Qrcode } from "html5-qrcode";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import MonitorService from "../../api/MonitorService";
import { AppointmentDetails } from "../../components/AppointmentDetails/AppointmentDetails";
import { BackButtonHeader } from "../../components/BackButtonHeader/BackButtonHeader";
import { BlockButton } from "../../components/BlockButton/BlockButton";
import { CustomButton } from "../../components/CustomButton/CustomButton";
import { CustomInput } from "../../components/CustomInput/CustomInput";
import { FloatingButtons } from "../../components/FloatingButtons/FloatingButtons";
import Modal from "../../components/Modal/Modal";
import { SubMenu } from "../../components/SubMenu/SubMenu";
import { MonitorIcon } from "../../icons/MonitorIcon/MonitorIcon";
import { AppLayout } from "../../layout/AppLayout/AppLayout";
import { goOneStepBack } from "../../services/NavigationService";
import { RootState } from "../../store";
import { adjustPinActions } from "../../store/adjust-pin-slice";
import { creatingMonitorActions } from "../../store/monitor-slice";
import styles from "../../styles/create-monitor.module.css";

export const DigitalMonitorSetup = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const { t } = useTranslation();

  const editMode = location.state?.editMode ?? false;
  const adjustPin = useSelector((state: RootState) => state.adjustPin);
  const monitorState = useSelector((state: RootState) => state.monitor);

  const monitor = editMode ? adjustPin : monitorState;
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const [scanning, setScanning] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newWirelessId, setNewWirelessId] = useState<string | null>(
    monitor.wirelessId
  );
  const [isWirelessChanged, setIsWirelessChanged] = useState(false);
  const [isDetachModalOpen, setIsDetachModalOpen] = useState(false);
  const [connectionEstablished, setConnectionEstablished] = useState(false);

  const handleBackButtonClicked = () => {
    if (isWirelessChanged && monitor.wirelessId !== newWirelessId) {
      if (editMode) {
        dispatch(
          adjustPinActions.setWirelessId({ wirelessId: monitor.wirelessId })
        );
        if (monitor.wirelessId === null) {
          dispatch(adjustPinActions.setIsWireless(false));
        }
      } else {
        dispatch(
          creatingMonitorActions.setWirelessId({
            wirelessId: monitor.wirelessId,
          })
        );
        if (monitor.wirelessId === null) {
          dispatch(creatingMonitorActions.setIsWireless());
        }
      }
    }

    if (monitor.wirelessId === null && newWirelessId === null) {
      if (editMode) dispatch(adjustPinActions.setIsWireless(false));
      else dispatch(creatingMonitorActions.setIsWireless());
    }
    goOneStepBack(navigate);
  };

  const handleInputChange = (input: string) => {
    setNewWirelessId(input);
    setIsWirelessChanged(true);
  };

  const handleScanClick = () => {
    setScanning(true);
  };

  useEffect(() => {
    if (!scanning) return;

    const qrRegionId = "qr-reader";

    const timeout = setTimeout(() => {
      if (document.getElementById(qrRegionId)) {
        const scanner = new Html5Qrcode(qrRegionId);
        scannerRef.current = scanner;

        scanner
          .start(
            { facingMode: "environment" },
            { fps: 10, qrbox: 250 },
            (decodedText) => {
              if (scannerRef.current) {
                scannerRef.current
                  .stop()
                  .then(() => {
                    setScanning(false);
                  })
                  .catch((err) => {
                    setScanning(false);
                  });
              }

              setNewWirelessId(decodedText);
            },
            (error) => {
              console.warn("QR Code scan failed:", error);
            }
          )
          .catch((err) => {
            console.error("QR Scanner init failed:", err);
            setScanning(false);
          });
      }
    }, 100);

    return () => {
      clearTimeout(timeout);
      if (scannerRef.current?.isScanning) {
        scannerRef.current
          .stop()
          .then(() => {
            scannerRef.current?.clear();
          })
          .catch((err) => {
            console.warn("Error while cleaning up scanner:", err);
          });
      }
    };
  }, [scanning]);

  const validateWirelessId = async () => {
    if (!newWirelessId) return;

    try {
      const response = await MonitorService.ValidateDigitalMonitor({
        id: newWirelessId,
      });

      if (response?.data?.valid) {
        setConnectionEstablished(true);

        if (editMode)
          dispatch(
            adjustPinActions.setWirelessId({
              wirelessId: newWirelessId,
            })
          );
        else
          dispatch(
            creatingMonitorActions.setWirelessId({
              wirelessId: newWirelessId,
            })
          );
      } else {
        setConnectionEstablished(false);
        if (editMode)
          dispatch(adjustPinActions.setWirelessId({ wirelessId: null }));
        else
          dispatch(creatingMonitorActions.setWirelessId({ wirelessId: null }));
      }

      setIsModalOpen(true);
    } catch (error) {
      console.error("Error validating monitor ID:", error);
    }
  };

  const goBackToMonitorCreation = () => {
    if (monitor.wirelessId === null) {
      if (editMode) dispatch(adjustPinActions.setIsWireless(false));
      else dispatch(creatingMonitorActions.setIsWireless());
    }
    setIsModalOpen(false);

    setTimeout(() => {
      goOneStepBack(navigate);
    }, 100);
  };

  const openModal = () => {
    setIsDetachModalOpen(true);
  };

  const removeConnection = () => {
    setNewWirelessId(null);
    if (editMode)
      dispatch(adjustPinActions.setWirelessId({ wirelessId: null }));
    else dispatch(creatingMonitorActions.setWirelessId({ wirelessId: null }));

    setIsDetachModalOpen(false);
  };

  return (
    <AppLayout hideHeader>
      <Modal
        isOpen={isModalOpen}
        message={
          connectionEstablished
            ? t("ConnectionEstablished")
            : t("SomethingWentWrong")
        }
        note={!connectionEstablished ? "CannotConfigureMonitor" : undefined}
        onConfirm={goBackToMonitorCreation}
        confirmText={connectionEstablished ? "Understood" : "Continue"}
        iconType={connectionEstablished ? "accept" : "alert"}
        confirmButtonColor={connectionEstablished ? "#069aa9" : "#3D5170"}
      />

      <Modal
        isOpen={isDetachModalOpen}
        message={t("DisconnectTraplinkedMessage")}
        note={"DisconnectTraplinkedNote"}
        confirmText={"Disconnect"}
        cancelText={"Cancel"}
        noteColor="#8493AB"
        iconType="detach"
        onConfirm={removeConnection}
        onCancel={() => {
          setIsDetachModalOpen(false);
        }}
        closeButtonColor={"#3D5170"}
        confirmButtonColor={"#CB3844"}
      />
      <div>
        <BackButtonHeader handleBackButtonClicked={handleBackButtonClicked} />
        <div className={styles["content-wrapper"]}>
          <AppointmentDetails />

          <div className={styles["content"]}>
            <SubMenu showSearchButton={false}>
              <span>
                {editMode ? "Kennnummer aktualisieren" : "Digitaler Monitor"}
              </span>
              <MonitorIcon color="dark" />
            </SubMenu>

            <BlockButton
              leftIcon="plus"
              hideLeftIcon={true}
              rightIcon="scan"
              onClick={handleScanClick}
            >
              QR Code scannen
            </BlockButton>

            {scanning && (
              <div
                id="qr-reader"
                style={{ width: "100%", maxWidth: 500, marginTop: 20 }}
              />
            )}

            <div style={{ marginTop: 20 }} />

            <CustomInput
              label="Kennnummer"
              placeholder="Kennnummer manuell eintragen"
              onInputChange={handleInputChange}
              onBlur={() => {}}
              initialValue={newWirelessId ? newWirelessId : undefined}
              maxLength={60}
              hideTick
            />
          </div>
        </div>

        <FloatingButtons>
          {newWirelessId !== null && (
            <CustomButton
              color="red"
              iconPosition="right"
              onClick={openModal}
              width={48}
              icon="detach"
            ></CustomButton>
          )}
          <CustomButton
            color="green"
            onClick={validateWirelessId}
            width={60}
            disabled={newWirelessId ? false : true}
          >
            {t("Confirm")}
          </CustomButton>
        </FloatingButtons>
      </div>
    </AppLayout>
  );
};
